// This program has been developed by students from the bachelor Computer Science
// at Utrecht University within the Software Project course. Copyright (c) Utrecht
// University (Department of Information and Computing Sciences)

import * as PIXI from "pixi.js";
import { gridBounds, gridCoordinates } from "./Types";

//Import all the sprites
import heart from "@/assets/images/levelMaker/Heart.svg";
import diamond from "@/assets/images/levelMaker/Diamond.svg";
import circle from "@/assets/images/levelMaker/Circle.svg";
import triangle from "@/assets/images/levelMaker/Triangle.svg";
import hexagon from "@/assets/images/levelMaker/Hexagon.svg";
import square from "@/assets/images/levelMaker/Square.svg";

const defaultLineStyle = {
  width: 1,
  color: 0x000,
  alpha: 1,
  alignment: 0.5,
  native: true,
};

export class PixiJSGrid extends PIXI.Graphics {
  _gridWidth: number;
  _gridHeight: number;
  _cellSize: number;

  constructor(width: number, height: number, cellSize: number) {
    super();

    this._gridWidth = width;
    this._gridHeight = height;
    this._cellSize = cellSize;

    const lConfig = {
      ...defaultLineStyle,
      ...{},
    };
    this.lineStyle(
      lConfig.width,
      lConfig.color,
      lConfig.alpha,
      lConfig.alignment,
      lConfig.native
    );
  }

  /**
   * Set cellsize
   * @param cellSize Cellsize
   */
  set cellSize(cellSize: number) {
    this._cellSize = cellSize || Math.sqrt(this._gridWidth);
  }

  /**
   * Get cellsize
   * @returns cellSize Cellsize
   */
  get cellSize(): number {
    return this._cellSize;
  }

  /**
   * Coordinates for each corner of the grid
   * The left (x1), right (xs), top (y1), bottom (y2)
   * @returns x1, y1, x2, y2
   */
  get bounds(): gridBounds {
    return {
      x1: this.x,
      y1: this.y,
      x2: this.x + this._gridWidth,
      y2: this.y + this._gridHeight,
    };
  }

  /**
   * The requested width of the grid given by the width constructor parameter
   * @returns Grid width
   */
  get originalWidth(): number {
    return this._gridWidth;
  }

  /**
   * Actual width of the grid
   * @returns Grid width
   */
  get gridWidth(): number {
    return this._gridWidth;
  }

  /**
   * Draw the grid and its tiles to the containing PIXI stage
   * @param width Grid width
   * @param height Grid height
   * @param cellSize Cellsize
   * @param gridTiles List of grid tiles
   */
  drawGrid(
    width: number,
    height: number,
    cellSize: number,
    gridTiles: gridCoordinates[]
  ): this {
    this.clearGrid();

    const cells = Math.ceil(Math.max(width, height) / cellSize) - 1;

    this.beginFill();

    //Draw the columns
    for (let i = 1; i <= cells; i += 1) {
      const startCoord = i * cellSize;
      this.moveTo(startCoord, 0);
      this.lineTo(startCoord, 50 * cellSize);
    }

    //Draw the rows
    for (let i = 1; i <= cells; i += 1) {
      const startCoord = i * cellSize;
      this.moveTo(0, startCoord);
      this.lineTo(50 * cellSize, startCoord);
    }

    this.endFill();

    let currentTextureSource = heart;
    let size;

    //Draw the tiles
    for (let i = 0; i < gridTiles.length; i++) {
      size = cellSize * 0.75;
      const splitObjects: string[] = gridTiles[i].object.split(";");
      switch (splitObjects[1]) {
        case "start":
          this.beginFill(0x29782f);
          this.drawRect(
            gridTiles[i].x * cellSize,
            gridTiles[i].y * cellSize,
            cellSize,
            cellSize
          );
          this.endFill();
          break;
        case "stop":
          this.beginFill(0xa12a2b);
          this.drawRect(
            gridTiles[i].x * cellSize,
            gridTiles[i].y * cellSize,
            cellSize,
            cellSize
          );
          this.endFill();
          break;
        default:
          this.beginFill(0x5c576b);
          this.drawRect(
            gridTiles[i].x * cellSize,
            gridTiles[i].y * cellSize,
            cellSize,
            cellSize
          );
          this.endFill();
          break;
      }

      switch (splitObjects[0]) {
        case "heart":
          currentTextureSource = heart;
          break;
        case "diamond":
          currentTextureSource = diamond;
          break;
        case "circle":
          currentTextureSource = circle;
          break;
        case "triangle":
          currentTextureSource = triangle;
          break;
        case "hexagon":
          currentTextureSource = hexagon;
          size = cellSize;
          break;
        case "square":
          currentTextureSource = square;
          break;
        default:
          continue;
      }

      //Draw the selected svg
      const texture = PIXI.Texture.from(currentTextureSource);
      const object = new PIXI.Sprite(texture);
      object.anchor.set(0.5);
      object.height = size;
      object.width = size;
      object.x = gridTiles[i].x * cellSize + cellSize / 2;
      object.y = gridTiles[i].y * cellSize + cellSize / 2;
      this.addChild(object);
    }

    return this;
  }

  //Clear the grid from the containing PIXI stage
  clearGrid(): this {
    const { width, alignment, color, alpha, native } = this.line;
    this.clear();
    this.lineStyle(width, color, alpha, alignment, native);
    //Remove all the svg's from the grid
    for (let i = this.children.length - 1; i >= 0; i--) {
      this.removeChild(this.children[i]);
    }
    return this;
  }

  /**
   * Transform global coordinates to grid coordinates
   * @param x x coordinate
   * @param y y coordinate
   * @param object Object
   * @returns x, y, object
   */
  getCellCoordinates(x: number, y: number, object: string): gridCoordinates {
    return {
      x: Math.floor((x - this.bounds.x1) / this.cellSize),
      y: Math.floor((y - this.bounds.y1) / this.cellSize),
      object: object,
    };
  }
}
